import { lazy, Suspense } from "react";
import { HelmetProvider } from "react-helmet-async";
import { AccessibilityProvider } from "@/components/accessibility/AccessibilityContext";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ScrollToTop } from "@/components/ScrollToTop";
import Index from "./pages/Index";

// Lazy-loaded routes for code splitting
const Fulfillment = lazy(() => import("./pages/Fulfillment"));
const PreFba = lazy(() => import("./pages/PreFba"));
const Lohnverpackung = lazy(() => import("./pages/Lohnverpackung"));
const Retouren = lazy(() => import("./pages/Retouren"));
const Greenmoose = lazy(() => import("./pages/Greenmoose"));
const Preise = lazy(() => import("./pages/Preise"));
const FAQ = lazy(() => import("./pages/FAQ"));
const UeberUns = lazy(() => import("./pages/UeberUns"));
const Kontakt = lazy(() => import("./pages/Kontakt"));
const Impressum = lazy(() => import("./pages/Impressum"));
const Datenschutz = lazy(() => import("./pages/Datenschutz"));
const Barrierefreiheit = lazy(() => import("./pages/Barrierefreiheit"));


const Onboarding = lazy(() => import("./pages/Onboarding"));
const Anfrage = lazy(() => import("./pages/Anfrage"));
const Dashboard = lazy(() => import("./pages/Dashboard"));

const Integrationen = lazy(() => import("./pages/Integrationen"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const SellerFulfilledPrime = lazy(() => import("./pages/SellerFulfilledPrime"));
const Aktionsversand = lazy(() => import("./pages/Aktionsversand"));
const Qualitaetsversprechen = lazy(() => import("./pages/Qualitaetsversprechen"));
const Fairplay = lazy(() => import("./pages/Fairplay"));
const Admin = lazy(() => import("./pages/Admin"));
const SEOPage = lazy(() => import("./pages/SEOPage"));
const RedirectHandler = lazy(() => import("./pages/RedirectHandler"));

const KundeWerden = lazy(() => import("./pages/KundeWerden"));
const Branchen = lazy(() => import("./pages/Branchen"));
const FulfillmentWechsel = lazy(() => import("./pages/FulfillmentWechsel"));
const Schnittstellen = lazy(() => import("./pages/Schnittstellen"));
const Karriere = lazy(() => import("./pages/Karriere"));
const Tools = lazy(() => import("./pages/Tools"));
const RetourenquotenBenchmark = lazy(() => import("./pages/RetourenquotenBenchmark"));
const PeakSeasonPlanerPage = lazy(() => import("./pages/PeakSeasonPlaner"));
const Podcast = lazy(() => import("./pages/Podcast"));

const CatchAllRedirect = lazy(() => import("./pages/CatchAllRedirect"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 30, // 30 Minuten – Daten gelten als "frisch"
      gcTime: 1000 * 60 * 60, // 60 Minuten – Cache wird gehalten
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  },
});

function PageFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-3">
        <div className="w-8 h-8 border-3 border-primary border-t-transparent rounded-full animate-spin" />
        <span className="text-sm text-muted-foreground">Laden…</span>
      </div>
    </div>
  );
}

const App = () => (
  <HelmetProvider>
    <AccessibilityProvider>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <ScrollToTop />
            <Suspense fallback={<PageFallback />}>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/fulfillment" element={<Fulfillment />} />
                <Route path="/pre-fba" element={<PreFba />} />
                <Route path="/lohnverpackung" element={<Lohnverpackung />} />
                <Route path="/retouren" element={<Retouren />} />
                <Route path="/greenmoose" element={<Greenmoose />} />
                <Route path="/preise" element={<Preise />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/ueber-uns" element={<UeberUns />} />
                <Route path="/kontakt" element={<Kontakt />} />
                <Route path="/impressum" element={<Impressum />} />
                <Route path="/datenschutz" element={<Datenschutz />} />
                <Route path="/barrierefreiheit" element={<Barrierefreiheit />} />

                <Route path="/kostenrechner" element={<Navigate to="/preise#rechner" replace />} />
                <Route path="/onboarding" element={<Onboarding />} />
                <Route path="/anfrage" element={<Anfrage />} />
                <Route path="/fulfillment-dashboard" element={<Dashboard />} />
                <Route path="/integrationen" element={<Integrationen />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route path="/seller-fulfilled-prime" element={<SellerFulfilledPrime />} />
                <Route path="/aktionsversand" element={<Aktionsversand />} />
                <Route path="/qualitaetsversprechen" element={<Qualitaetsversprechen />} />
                <Route path="/fairplay" element={<Fairplay />} />
                <Route path="/admin" element={<Admin />} />
                <Route path="/s/:slug" element={<SEOPage />} />
                <Route path="/r/:slug" element={<RedirectHandler />} />
                
                <Route path="/kunde-werden" element={<KundeWerden />} />
                <Route path="/branchen" element={<Branchen />} />
                <Route path="/fulfillment-wechseln" element={<FulfillmentWechsel />} />
                <Route path="/schnittstellen" element={<Schnittstellen />} />
                <Route path="/karriere" element={<Karriere />} />
                <Route path="/tools" element={<Tools />} />
                <Route path="/retourenquoten-benchmark" element={<RetourenquotenBenchmark />} />
                <Route path="/peak-season-planer" element={<PeakSeasonPlanerPage />} />
                <Route path="/podcast" element={<Podcast />} />
                {/* Legacy redirects from old WordPress site */}
                <Route path="/anbindung-shops-und-marktplaetze" element={<Navigate to="/integrationen" replace />} />
                <Route path="/fulfillment-service" element={<Navigate to="/fulfillment" replace />} />
                <Route path="/versandunternehmen" element={<Navigate to="/fulfillment" replace />} />
                <Route path="/unser-fairplay" element={<Navigate to="/fairplay" replace />} />

                <Route path="/shop" element={<Navigate to="/" replace />} />
                <Route path="/greenmoose-umweltschutz" element={<Navigate to="/greenmoose" replace />} />
                <Route path="/fullfillment-faq" element={<Navigate to="/faq" replace />} />
                <Route path="/fulfillment-preise" element={<Navigate to="/preise" replace />} />
                <Route path="/fulfillment-kosten" element={<Navigate to="/preise" replace />} />
                <Route path="/fulfillment-loesungen" element={<Navigate to="/fulfillment" replace />} />
                <Route path="/fulfillment-kostenrechner" element={<Navigate to="/preise#rechner" replace />} />
                <Route path="/fulfillment-dienstleister" element={<Navigate to="/fulfillment" replace />} />
                <Route path="/fulfillment-center" element={<Navigate to="/fulfillment" replace />} />
                <Route path="/fulfillment-anfrage" element={<Navigate to="/anfrage" replace />} />
                <Route path="/fulfillment-lagerhaltung" element={<Navigate to="/fulfillment" replace />} />
                <Route path="/online-versand" element={<Navigate to="/fulfillment" replace />} />
                <Route path="/onlineshop-logistik" element={<Navigate to="/fulfillment" replace />} />
                <Route path="/pre-fba-anfrage" element={<Navigate to="/anfrage" replace />} />
                <Route path="/amazon-pre-fba-zwischenlager" element={<Navigate to="/pre-fba" replace />} />
                <Route path="/aktionsversand-optimieren" element={<Navigate to="/aktionsversand" replace />} />

                <Route path="/problemmeldung-dpd-plugin" element={<Navigate to="/kontakt" replace />} />
                <Route path="/newsletter-archiv" element={<Navigate to="/blog" replace />} />
                <Route path="/widerrufsbelehrung" element={<Navigate to="/" replace />} />
                <Route path="/billbee-fulfillment-integration" element={<Navigate to="/integrationen" replace />} />
                <Route path="/woocommerce-snippets" element={<Navigate to="/tools" replace />} />
                <Route path="/zahlungsarten" element={<Navigate to="/" replace />} />
                <Route path="/mein-konto" element={<Navigate to="/" replace />} />
                <Route path="/lucid-registrierungsnummer" element={<Navigate to="/" replace />} />
                {/* Old SEO pages → new /s/ paths */}
                <Route path="/warum-ein-zentrales-lager-mit-uns-die-bessere-wahl-fuer-deinen-versand-ist" element={<Navigate to="/s/warum-ein-zentrales-lager-mit-uns-die-bessere-wahl-fuer-deinen-versand-ist" replace />} />
                <Route path="/schnelle-lieferung-online-shop" element={<Navigate to="/s/schnelle-lieferung-online-shop-emoose-dein-versandpartner-fuer-blitzschnelle-bestellungen" replace />} />
                <Route path="/nachteile-amazon-multichannel-versand" element={<Navigate to="/s/nachteile-amazon-multichannel-versand" replace />} />
                <Route path="/marketing-konzept-fuer-einen-online-shop" element={<Navigate to="/s/marketing-konzept-fuer-einen-online-shop" replace />} />
                <Route path="/konfektionierung-dein-partner-fuer-massgeschneiderte-loesungen" element={<Navigate to="/s/konfektionierung-dein-partner-fuer-massgeschneiderte-loesungen" replace />} />
                {/* Diese drei Pfade werden zentral über die Redirect-Datenbank gepflegt */}
                <Route path="/fulfillment-preise-kosten-optimieren" element={<Navigate to="/s/fulfillment-preise-kosten-enthuellt-die-7-schluesselfaktoren-fuer-deine-kalkulation" replace />} />
                <Route path="/fulfillment-kosten-optimieren" element={<Navigate to="/s/fulfillment-kosten-im-griff-10-effektive-strategien-zur-optimierung-deiner-fulfillment-kosten" replace />} />
                <Route path="/fulfillment-dienstleister-dein-partner-fuer-reibungslosen-e-commerce" element={<Navigate to="/s/fulfillment-dienstleister-dein-partner-fuer-reibungslosen-e-commerce" replace />} />
                <Route path="/fulfillment-center-vorteile-kosten-emoose" element={<Navigate to="/s/fulfillment-center-9-gruende-warum-dein-online-business-jetzt-profitieren-sollte" replace />} />
                <Route path="/fulfillment-by-merchant-strategien-erfolg" element={<Navigate to="/s/fulfillment-by-merchant-7-geniale-strategien-fuer-deinen-ultimativen-erfolg" replace />} />
                <Route path="/fulfillment-an-dienstleister-auslagern" element={<Navigate to="/s/fulfillment-an-dienstleister-auslagern" replace />} />
                <Route path="/externes-fulfillment-fuer-nahrungsergaenzungsmittel-optimiere-deinen-versand-und-steigere-deine-effizienz" element={<Navigate to="/s/externes-fulfillment-fuer-nahrungsergaenzungsmittel-optimiere-deinen-versand-und-steigere-deine-effizienz" replace />} />
                <Route path="/fulfillment-startup-emoose-logistik" element={<Navigate to="/s/explosives-wachstum-7-vorteile-von-fulfillment-fuer-startups" replace />} />
                <Route path="/emoose-2/e-commerce-logistik-fulfillment" element={<Navigate to="/s/e-commerce-fulfillment-alles-was-du-wissen-musst" replace />} />
                <Route path="/e-commerce-fulfillment-alles-was-du-wissen-musst" element={<Navigate to="/s/e-commerce-fulfillment-alles-was-du-wissen-musst" replace />} />
                <Route path="/onlinehandel-logistik-optimieren" element={<Navigate to="/s/die-7-ultimativen-geheimnisse-fuer-eine-revolutionaere-onlinehandel-logistik" replace />} />
                <Route path="/dein-fulfillment-anbieter-fuer-effizientes-versandmanagement" element={<Navigate to="/s/dein-fulfillment-anbieter-fuer-effizientes-versandmanagement" replace />} />
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<CatchAllRedirect />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </TooltipProvider>
      </QueryClientProvider>
    </AccessibilityProvider>
  </HelmetProvider>
);

export default App;
